import { useTranslation } from 'react-i18next';

import { readQuantity } from 'support/helpers/packingList/packableQuantity';
import { SearchInput } from 'components/SearchInput/SearchInput';

import { useLineItemSearch } from './hooks/useLineItemSearch';
import { LineItemEntry, LineItemId } from '../../types';
import { ProductCard } from './ProductCard';

type ProductsListProps = {
  products: Array<LineItemEntry>;
  remainingByLineItem: Map<LineItemId, number>;
  quantityFieldPath: string;
  placedCount: number;
  totalCount: number;
  selectedIds: Set<string>;
  onToggleSelect: (id: LineItemId) => void;
  onClearSelection: () => void;
  onMoveTo: (lineItemId: LineItemId) => void;
};

export const ProductsList = ({
  products,
  remainingByLineItem,
  quantityFieldPath,
  placedCount,
  totalCount,
  selectedIds,
  onToggleSelect,
  onClearSelection,
  onMoveTo,
}: ProductsListProps) => {
  const { t } = useTranslation();
  const { query, setQuery, filteredProducts, showNoResults } = useLineItemSearch(products);

  return (
    <div className="flex w-1/3 shrink-0 flex-col overflow-hidden border-r border-gray-200">
      <div className="shrink-0 px-6 py-4">
        <p className="text-xs font-medium tracking-wide text-gray-500 uppercase">
          {t('webedi:packing_list.products.title')}
        </p>
        <p className="text-xs text-gray-500">
          {t('webedi:packing_list.products.remaining', { remaining: totalCount - placedCount, total: totalCount })}
        </p>
        {selectedIds.size > 0 ? (
          <p className="mt-1 flex items-center gap-2 text-xs text-gray-500">
            <span>{t('webedi:packing_list.products.selected_count', { count: selectedIds.size })}</span>
            <button type="button" className="text-procuros-green-600 hover:underline" onClick={onClearSelection}>
              {t('webedi:packing_list.products.clear_selection')}
            </button>
          </p>
        ) : null}
        <SearchInput
          filter={query}
          onFilterChange={setQuery}
          placeholder={t('webedi:packing_list.products.search')}
          className="mt-2 w-full"
        />
      </div>
      <div className="flex-1 space-y-2 overflow-y-auto px-6 pt-2 pb-6">
        {products.length === 0 ? (
          // Distinct from no_results: there is nothing to pack at all, rather than nothing matching
          // the search. Without this the panel is simply blank and the user is left guessing why.
          <div className="pt-2">
            <p className="text-xs font-medium text-gray-700">{t('webedi:packing_list.products.empty.title')}</p>
            <p className="mt-1 text-xs text-gray-500">{t('webedi:packing_list.products.empty.description')}</p>
          </div>
        ) : showNoResults ? (
          <p className="pt-2 text-xs text-gray-500">{t('webedi:packing_list.products.no_results')}</p>
        ) : (
          filteredProducts.map((product) => {
            const total = readQuantity(product.row[quantityFieldPath]);
            const available = remainingByLineItem.get(product.id) ?? total;
            return (
              <ProductCard
                key={product.id}
                product={product}
                available={available}
                total={total}
                isSelected={selectedIds.has(product.id)}
                onToggleSelect={() => onToggleSelect(product.id)}
                onMoveTo={() => onMoveTo(product.id)}
              />
            );
          })
        )}
      </div>
    </div>
  );
};

/**
 * i18n keys
 * t('webedi:packing_list.products.selected_count', { count })
 * t('webedi:packing_list.products.clear_selection')
 */
